import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../../helpers/apiUtils';
import { formatBerlin, statusChipColor } from './shared';
import SamHtmlPreviewDialog, { SamHtmlPreviewData } from './SamHtmlPreviewDialog';

type Phase = 'audience' | 'editorial';

interface ScoredCandidate {
  asset_id: number;
  isin: string;
  name: string;
  /** `assets.type` ENUM — drives the asset-type chip + per-ISIN routing in the picker. */
  asset_type: string;
  report_img_url: string;
  editorial_score: number;
  signups_in_window: number;
  /** Long-window (180d) mc_members signups for this ISIN. Added 2026-06-08 with the mixed-window picker. */
  signups_long_window: number;
  /** `total_score` since 2026-06-08 is the mixed_rank `0.5 × sigs_14d + 0.5 × sigs_180d × 14/180` (float), not an integer. */
  total_score: number;
  phase: Phase;
  score_date: string;
}

interface RotationEntry {
  client_code: string;
  last_sent_date: string | null;
  days_since_last_send: number | null;
  rotation_order: number;
  active: boolean;
  is_next_in_line: boolean;
}

type DryRunSkipReason =
  // `webinar_heatup_planned` + `plx_mailing_planned` retired 2026-05-28 — AA
  // audience disjoint with PLX/Webinar via the Asset-Analysis Excl: GEV+I
  // segment, so the scheduler no longer skips on those gates. See backend
  // route at `node-app/src/routes/asset-selection.ts`.
  'already_scheduled_for_client' | 'product_collision_other_client';

interface ForecastResponse {
  score_date: string | null;
  score_date_is_today: boolean;
  rotation_state: RotationEntry[];
  next_in_line: {
    client_code: string | null;
    send_date: string;
    phase_used: Phase | null;
    pick: ScoredCandidate | null;
    top_candidates: ScoredCandidate[];
    reason_if_null: string | null;
    dry_run_skip_reason: DryRunSkipReason | null;
    colliding_client_code: string | null;
  };
}

const DRY_RUN_SKIP_REASON_LABEL: Record<DryRunSkipReason, string> = {
  already_scheduled_for_client:
    'Andere Asset-Analyse-Sendung für diesen Client am Sendetag bereits geplant',
  product_collision_other_client: 'Produkt 100013 bereits bei anderem Client für diesen Tag',
};

interface HistoryRow {
  id: number;
  client_code: string;
  send_date: string;
  status: string;
  asset_id: number | null;
  asset_isin: string | null;
  asset_name: string | null;
  affiliate: string | null;
  lp_url: string | null;
  mc_campaign_id: string | null;
  mc_campaign_title: string | null;
  dup_send: number;
  inactive_send: number;
  recipients: number | null;
  unique_opens: number | null;
  unique_clicks: number | null;
  unsubscribed: number | null;
}

const CLIENT_FILTER_OPTIONS = ['bg', 'be', 'sw', 'tt'];
const DAY_RANGE_OPTIONS = [30, 60, 90];

const formatPct = (count: number | null, total: number | null): string => {
  if (count == null || total == null || total === 0) return '—';
  return `${((count / total) * 100).toFixed(1)} %`;
};

const formatNumber = (value: number | null): string => {
  if (value == null) return '—';
  return value.toLocaleString('de-DE');
};

const phaseChipColor = (phase: Phase | null): 'success' | 'info' | 'default' => {
  if (phase === 'audience') return 'success';
  if (phase === 'editorial') return 'info';
  return 'default';
};

const phaseLabel = (phase: Phase | null): string => {
  if (phase === 'audience') return 'audience';
  if (phase === 'editorial') return 'editorial';
  return '–';
};

/**
 * German short labels for `assets.type` ENUM values. Renders a chip in the
 * pick card + a column in the top-candidates table so operators can spot
 * non-stock picks at a glance (per-ISIN SAM authoring + custom copy work
 * shipped 2026-05-28).
 */
const assetTypeChipLabel = (assetType: string | null | undefined): string => {
  switch ((assetType ?? '').toLowerCase()) {
    case 'cryptocurrency':
    case 'crypto':
      return 'Krypto';
    case 'commodity':
      return 'Rohstoff';
    case 'etf':
      return 'ETF';
    case 'fund':
      return 'Fonds';
    case 'index':
      return 'Index';
    case 'forex':
      return 'Devisen';
    case 'stock':
      return 'Aktie';
    case 'etc':
      return 'ETC';
    default:
      return assetType || '—';
  }
};

const assetTypeChipColor = (
  assetType: string | null | undefined,
): 'default' | 'warning' | 'info' | 'success' => {
  switch ((assetType ?? '').toLowerCase()) {
    case 'cryptocurrency':
    case 'crypto':
      return 'warning';
    case 'commodity':
      return 'info';
    case 'stock':
      return 'default';
    default:
      return 'success';
  }
};

const AssetSelectionTab: React.FC = () => {
  const [forecast, setForecast] = useState<ForecastResponse | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [historyDays, setHistoryDays] = useState(60);
  const [historyClient, setHistoryClient] = useState('');

  const [preview, setPreview] = useState<SamHtmlPreviewData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handlePreview = async (id: number) => {
    setError(null);
    try {
      const { data } = await api.get(`/api/adserver/external-sams/preview/${id}`);
      if (data?.success) {
        setPreview(data.data as SamHtmlPreviewData);
        setPreviewOpen(true);
      } else {
        setError(data?.error ?? 'Vorschau fehlgeschlagen');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const loadForecast = useCallback(async () => {
    setLoadingForecast(true);
    setError(null);
    try {
      const { data } = await api.get('/api/adserver/asset-selection/forecast');
      setForecast(data?.data ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoadingForecast(false);
    }
  }, []);

  useEffect(() => {
    loadForecast();
  }, [loadForecast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const params = new URLSearchParams();
        params.set('days', String(historyDays));
        if (historyClient) params.set('client_code', historyClient);
        const { data } = await api.get(`/api/adserver/asset-selection/history?${params}`);
        if (!cancelled) setHistory(data?.data ?? []);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [historyDays, historyClient]);

  const nextInLine = forecast?.next_in_line ?? null;
  const rotationCursorWarning = useMemo(() => {
    if (!nextInLine?.client_code || !forecast) return null;
    const row = forecast.rotation_state.find((r) => r.client_code === nextInLine.client_code);
    if (!row?.days_since_last_send || row.days_since_last_send <= 5) return null;
    return `Rotation-Cursor seit ${row.days_since_last_send} Tagen nicht bewegt — Cron-Stau?`;
  }, [nextInLine, forecast]);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Section A: Health banner */}
      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
        {forecast?.score_date && forecast.score_date_is_today && (
          <Chip
            size="small"
            color="success"
            label={`Editorial-Score: heute (${forecast.score_date})`}
          />
        )}
        {forecast?.score_date && !forecast.score_date_is_today && (
          <Alert severity="warning" sx={{ py: 0 }}>
            Editorial-Score-Datum: {forecast.score_date} (Fallback). Editorial-Cron hat heute noch
            nichts geliefert.
          </Alert>
        )}
        {rotationCursorWarning && (
          <Alert severity="warning" sx={{ py: 0 }}>
            {rotationCursorWarning}
          </Alert>
        )}
      </Stack>

      {/* Section B: How is it decided? */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Wie wird ausgewählt?</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" component="div">
            <strong>Rotation:</strong> Cursor pro Tag, ein Client pro Versand. Reihenfolge: noch nie
            gesendet → ältestes <code>last_sent_date</code> → <code>rotation_order</code> als
            Seed-Tiebreaker.
            <br />
            <strong>Phase 1 (audience-aware):</strong>{' '}
            <code>editorial_score × signups_14d (mc_members)</code>. Editorial-Score ≥ 30.
            <br />
            <strong>Phase 2 (Fallback):</strong> falls Phase 1 leer (keine Audience-Signups), nur{' '}
            <code>editorial_score</code>, weiterhin ≥ 30.
            <br />
            <strong>Cooldowns:</strong> 14 Tage pro Client, 5 Tage global (über alle Clients).
            <br />
            <strong>Filter:</strong> ISIN muss in <code>editorialtool_history</code> stehen und ein
            Report-Bild in <code>lp_einzel_analyse_report_imgs</code> haben.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Section C: Next pick */}
      {loadingForecast ? (
        <Box sx={{ my: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : forecast ? (
        <>
          <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
            <Typography variant="h6" gutterBottom>
              Nächste Auswahl
            </Typography>
            {nextInLine?.dry_run_skip_reason && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Cron-Gate wird zur Sendezeit greifen:</strong>{' '}
                {DRY_RUN_SKIP_REASON_LABEL[nextInLine.dry_run_skip_reason]}
                {nextInLine.dry_run_skip_reason === 'product_collision_other_client' &&
                  nextInLine.colliding_client_code && (
                    <> (kollidiert mit {nextInLine.colliding_client_code.toUpperCase()})</>
                  )}
                . Asset-Analysis-Versand für{' '}
                <strong>{nextInLine.client_code?.toUpperCase()}</strong> am{' '}
                <strong>{nextInLine.send_date}</strong> wird mit{' '}
                <code>skipReason={nextInLine.dry_run_skip_reason}</code> übersprungen — die Rotation
                rückt erst beim nächsten erfolgreichen Versand vor.
              </Alert>
            )}
            {!nextInLine?.dry_run_skip_reason && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Hinweis: Mailchimp-Konflikt-Scan (selbe Liste + Datum) läuft erst zur Cron-Zeit.
              </Typography>
            )}
            {nextInLine?.client_code === null ? (
              <Alert severity="warning">
                Kein aktiver Rotations-Client. Prüfe{' '}
                <code>external_asset_analysis_rotation.active</code>.
              </Alert>
            ) : nextInLine?.pick === null ? (
              <Alert severity="warning">
                Kein Asset für <strong>{nextInLine?.client_code?.toUpperCase()}</strong> am{' '}
                <strong>{nextInLine?.send_date}</strong> wählbar — alle Kandidaten im Cooldown oder
                unter Score 30. Reason: <code>{nextInLine?.reason_if_null ?? 'unknown'}</code>.
              </Alert>
            ) : (
              nextInLine?.pick && (
                <Box display="flex" gap={2} alignItems="flex-start">
                  {nextInLine.pick.report_img_url && (
                    <Box
                      component="img"
                      src={nextInLine.pick.report_img_url}
                      alt={nextInLine.pick.name}
                      sx={{ width: 120, height: 'auto', borderRadius: 1, flexShrink: 0 }}
                    />
                  )}
                  <Box flex={1}>
                    <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip
                        size="small"
                        color="primary"
                        label={nextInLine.client_code?.toUpperCase()}
                      />
                      <Chip
                        size="small"
                        color={assetTypeChipColor(nextInLine.pick.asset_type)}
                        label={assetTypeChipLabel(nextInLine.pick.asset_type)}
                      />
                      <Chip
                        size="small"
                        color={phaseChipColor(nextInLine.phase_used)}
                        label={`Phase: ${phaseLabel(nextInLine.phase_used)}`}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Geplant für {nextInLine.send_date} (06:00 Berlin)
                      </Typography>
                    </Stack>
                    <Typography variant="h5">{nextInLine.pick.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      ISIN <code>{nextInLine.pick.isin}</code>
                    </Typography>
                    <Typography variant="body2">
                      Editorial-Score <strong>{nextInLine.pick.editorial_score.toFixed(0)}</strong>
                      {nextInLine.phase_used === 'audience' && (
                        <>
                          {' '}
                          × Signups (14d) <strong>{nextInLine.pick.signups_in_window}</strong> ={' '}
                          <strong>{nextInLine.pick.total_score.toFixed(0)}</strong>
                        </>
                      )}
                    </Typography>
                  </Box>
                </Box>
              )
            )}
          </Paper>

          {/* Section D: Top candidates */}
          {nextInLine?.top_candidates && nextInLine.top_candidates.length > 0 && (
            <Paper sx={{ mb: 2, backgroundColor: '#ffffff' }}>
              <Box sx={{ p: 2, pb: 0 }}>
                <Typography variant="subtitle2">
                  Top Kandidaten für {nextInLine.client_code?.toUpperCase()} (sortiert nach Score)
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        '#',
                        'ISIN',
                        'Name',
                        'Typ',
                        'Phase',
                        'Editorial',
                        'Signups (14d)',
                        'Signups (180d)',
                        'Mixed Rank',
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nextInLine.top_candidates.map((c, idx) => (
                      <TableRow
                        key={c.asset_id}
                        hover
                        sx={idx === 0 ? { backgroundColor: '#e8f5e9' } : undefined}
                      >
                        <TableCell>
                          {idx === 0 ? <Chip size="small" color="success" label="1" /> : idx + 1}
                        </TableCell>
                        <TableCell>
                          <code>{c.isin}</code>
                        </TableCell>
                        <TableCell>{c.name}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={assetTypeChipColor(c.asset_type)}
                            label={assetTypeChipLabel(c.asset_type)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={phaseChipColor(c.phase)}
                            label={phaseLabel(c.phase)}
                          />
                        </TableCell>
                        <TableCell>{c.editorial_score.toFixed(0)}</TableCell>
                        <TableCell>{c.phase === 'audience' ? c.signups_in_window : '—'}</TableCell>
                        <TableCell>
                          {c.phase === 'audience' ? c.signups_long_window : '—'}
                        </TableCell>
                        <TableCell>
                          <strong>{c.total_score.toFixed(2)}</strong>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Section E: Rotation state */}
          <Paper sx={{ mb: 2, backgroundColor: '#ffffff' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="subtitle2">Rotation</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Client', 'Zuletzt gesendet', 'Tage her', 'Order', 'Aktiv', 'Nächster?'].map(
                      (h) => (
                        <TableCell
                          key={h}
                          sx={{
                            backgroundColor: '#e0e0e0 !important',
                            color: 'black',
                            fontWeight: 'bold',
                          }}
                        >
                          {h}
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {forecast.rotation_state.map((r) => (
                    <TableRow key={r.client_code} hover>
                      <TableCell>{r.client_code.toUpperCase()}</TableCell>
                      <TableCell>{r.last_sent_date ?? '—'}</TableCell>
                      <TableCell>
                        {r.days_since_last_send == null ? '—' : `${r.days_since_last_send} d`}
                      </TableCell>
                      <TableCell>{r.rotation_order}</TableCell>
                      <TableCell>
                        {r.active ? (
                          <Chip size="small" color="success" label="ja" />
                        ) : (
                          <Chip size="small" label="nein" />
                        )}
                      </TableCell>
                      <TableCell>
                        {r.is_next_in_line && (
                          <Chip size="small" color="primary" label="Nächster" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : null}

      {/* Section F: History */}
      <Paper sx={{ backgroundColor: '#ffffff' }}>
        <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle2">Verlauf</Typography>
          <Select
            size="small"
            value={historyDays}
            onChange={(e) => setHistoryDays(Number(e.target.value))}
          >
            {DAY_RANGE_OPTIONS.map((d) => (
              <MenuItem key={d} value={d}>
                Letzte {d} Tage
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            displayEmpty
            value={historyClient}
            onChange={(e) => setHistoryClient(e.target.value as string)}
          >
            <MenuItem value="">Alle Clients</MenuItem>
            {CLIENT_FILTER_OPTIONS.map((c) => (
              <MenuItem key={c} value={c}>
                {c.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {loadingHistory ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {[
                    'Sendezeit',
                    'Client',
                    'Status',
                    'ISIN',
                    'Asset',
                    'Empfänger',
                    'Opens',
                    'Open-Rate',
                    'Clicks',
                    'Click-Rate',
                    'Unsubs',
                    'Aktion',
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        backgroundColor: '#e0e0e0 !important',
                        color: 'black',
                        fontWeight: 'bold',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={12} align="center">
                      Keine Asset-Analyse-Sendungen im gewählten Zeitraum.
                    </TableCell>
                  </TableRow>
                )}
                {history.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatBerlin(row.send_date)}</TableCell>
                    <TableCell>{row.client_code.toUpperCase()}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusChipColor(row.status)} />
                    </TableCell>
                    <TableCell>
                      <code>{row.asset_isin ?? '—'}</code>
                    </TableCell>
                    <TableCell>{row.asset_name ?? '—'}</TableCell>
                    <TableCell>{formatNumber(row.recipients)}</TableCell>
                    <TableCell>{formatNumber(row.unique_opens)}</TableCell>
                    <TableCell>{formatPct(row.unique_opens, row.recipients)}</TableCell>
                    <TableCell>{formatNumber(row.unique_clicks)}</TableCell>
                    <TableCell>{formatPct(row.unique_clicks, row.recipients)}</TableCell>
                    <TableCell>{formatNumber(row.unsubscribed)}</TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => handlePreview(row.id)}>
                        Vorschau
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <SamHtmlPreviewDialog
        open={previewOpen}
        preview={preview}
        onClose={() => setPreviewOpen(false)}
      />
    </Box>
  );
};

export default AssetSelectionTab;
